import { cn } from "@pushup/ui";
import { useWorkoutStore } from "@/state/workout-store";

interface DepthBarProps {
  /** Full pushup target elbow bend; default 92°. */
  targetDepthDeg?: number;
  className?: string;
}

/**
 * Vertical DEPTH meter.
 *
 * Fill rises from the bottom as the user descends:
 *   0%  = arms fully straight (180°)
 *   100% = target elbow bend reached (≤ targetDepthDeg)
 */
export function DepthBar({ targetDepthDeg = 92, className }: DepthBarProps) {
  const currentDepthDeg = useWorkoutStore((s) => s.currentDepthDeg);

  const percent = Math.min(
    100,
    Math.max(0, ((180 - currentDepthDeg) / (180 - targetDepthDeg)) * 100),
  );

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <span className="text-[10px] font-semibold uppercase tracking-widest text-white/60">
        Depth
      </span>

      <div className="relative w-3.5 flex-1 overflow-hidden rounded-full bg-white/15 shadow-inner">
        <div
          className={cn(
            "absolute inset-x-0 bottom-0 transition-[height] duration-150",
            percent >= 100
              ? "bg-gradient-to-t from-emerald-600 to-emerald-400"
              : "bg-gradient-to-t from-amber-600 to-amber-400",
          )}
          style={{ height: `${percent}%` }}
        />
      </div>

      <div className="flex flex-col items-center text-white">
        <span className="font-mono text-sm font-bold tabular-nums">
          {Math.round(currentDepthDeg)}°
        </span>
        <span className="text-[9px] uppercase tracking-wider text-white/50">
          target ≤{targetDepthDeg}°
        </span>
      </div>
    </div>
  );
}
